import {
  createContext,
  useState,
  FunctionComponent,
  useContext,
  useCallback,
  useMemo,
} from 'react'
import { Card, CardInformation, AddressInformation } from '../classes/Card'
import { setCache } from '@/utils/cache'
interface IPrice {
  price: number
}
export interface ICheckoutContext {
  card: Card
  setCard: (card: Card) => void
  cardInformation: CardInformation
  setCardInformation: (CardInformation) => void
  paymentAddress: AddressInformation
  setPaymentAddress: (AddressInformation) => void
  attendancePrice: IPrice
  setAttendancePrice: (IPrice) => void
}
const CheckoutContext = createContext<ICheckoutContext>(null)

export const CheckoutProvider: FunctionComponent = ({ children }) => {
  const [cardState, setCardState] = useState<Card>()
  const setCard = useCallback((card: Card) => {
    setCardState(card)
    setCache('card', card)
  }, [])

  const [cardInformationState, setCardInformationState] =
    useState<CardInformation>(
      new CardInformation({
        cardNumber: '',
        holderName: '',
        expirationDate: '',
        cvv: '',
        isDefault: false,
      }),
    )
  const setCardInformation = useCallback((cardInformation: CardInformation) => {
    setCardInformationState(cardInformation)
    setCache('cardInformation', cardInformation)
  }, [])

  const [paymentAddressState, setPaymentAddressState] =
    useState<AddressInformation>(
      new AddressInformation({
        cep: '',
        streetName: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        country: '',
      }),
    )
  const setPaymentAddress = useCallback(
    (paymentAddress: AddressInformation) => {
      setPaymentAddressState(paymentAddress)
      setCache('paymentAddress', paymentAddress)
    },
    [],
  )

  const [price, setPrice] = useState<IPrice>({ price: null })
  const setAttendancePrice = useCallback((paramPrice: IPrice) => {
    setPrice(paramPrice)
  }, [])

  const checkoutContextValue = useMemo(() => {
    return {
      card: cardState,
      setCard,
      cardInformation: cardInformationState,
      setCardInformation,
      paymentAddress: paymentAddressState,
      setPaymentAddress,
      attendancePrice: price,
      setAttendancePrice,
    }
  }, [
    cardInformationState,
    cardState,
    paymentAddressState,
    price,
    setAttendancePrice,
    setCard,
    setCardInformation,
    setPaymentAddress,
  ])

  return (
    <CheckoutContext.Provider value={checkoutContextValue}>
      {children}
    </CheckoutContext.Provider>
  )
}

export const useCheckoutContext = (): ICheckoutContext => {
  const context = useContext(CheckoutContext)
  if (!context) {
    throw new Error('useCheckoutContext must be used within a CheckoutProvider')
  }
  return context
}
